import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CropEmbeddingSearchResponse } from '../../../types';

interface SelectEmbeddingViewProps {
  embeddings: CropEmbeddingSearchResponse[];
  targetType: 'primary' | 'alternative';
  loadingEmbeddingId: string | null;
  onSelect: (embedding: CropEmbeddingSearchResponse) => void;
  onBack: () => void;
}

export const SelectEmbeddingView: React.FC<SelectEmbeddingViewProps> = ({
  embeddings,
  targetType,
  loadingEmbeddingId,
  onSelect,
  onBack,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded-full">
          <ArrowBackIcon />
        </button>
        <h3 className="text-lg font-bold">Select Embedding</h3>
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded border ${
            targetType === 'primary'
              ? 'border-green-600 text-green-600'
              : 'border-purple-600 text-purple-600'
          }`}
        >
          {targetType === 'primary' ? 'Primary' : 'Alternative'}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Click an embedding to add it as{' '}
        <span className={targetType === 'primary' ? 'text-green-600' : 'text-purple-600'}>
          {targetType} image
        </span>
        .
      </p>

      {/* Embeddings Grid */}
      <div className="grid grid-cols-4 gap-4 overflow-y-auto flex-1">
        {embeddings.map((embedding) => {
          const isLoading = loadingEmbeddingId === embedding.productEmbeddingCropId;
          const isDisabled = loadingEmbeddingId !== null;
          return (
            <div
              key={embedding.productEmbeddingCropId}
              onClick={() => !isDisabled && onSelect(embedding)}
              className={`relative rounded-lg overflow-hidden border ${
                embedding.isActive ? 'border-gray-300' : 'border-red-300'
              } ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-green-500'} transition-colors`}
            >
              <div className="aspect-square flex items-center justify-center bg-white">
                <img
                  src={embedding.vizUrl}
                  alt={`Embedding ${embedding.productEmbeddingCropId}`}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {/* Hover overlay with checkmark */}
              {!isDisabled && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-20">
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#D946EF' }} />
                </div>
              )}

              {/* Badges */}
              <div className="flex items-center gap-1 mt-1 px-1 pb-1">
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    embedding.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {embedding.isActive ? 'Active' : 'Inactive'}
                </span>
                <span
                  className="px-2 py-0.5 text-xs font-medium rounded capitalize"
                  style={{ backgroundColor: '#CEC9E8', color: '#27272E' }}
                >
                  {embedding.classificationType}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
